import { useEffect, useState } from "react";
import Button, { ButtonType } from "../../components/Button";
import InfoIcon from "../../components/infoIcon/InfoIcon";
import LoadingSpinner from "../../components/loadingSpinner/LoadingSpinner";
import MessageModal, { MessageModalType } from "../../components/modals/MessageModal";
import useFetch from "../../hooks/useFetch";
import { getFetchOptions } from "../../utils/fetch";
import { isEmailValid, isTextValid, objectsEqual } from "../../utils/validation";
import AuthLogoutMessage from "../Auth/components/AuthLogoutMessage";
import { LogoutReason, useSession } from "../Auth/hooks/useSession";
import "./WhiteLabelling.scss";
import ColourControl from "./components/ColourControl";
import EmailScreen from "./components/EmailScreen";
import Screen from "./components/Screen";
import useUnsavedChangesPrompt from "../../hooks/useUnsavedChangesPrompt";

const IMAGE_SIZE_LIMIT = 2097152; // 2MB
const MINIMUM_PHONE_NUMBER_LENGTH = 3;
const DefaultWhiteLabellingSettings = {
	Id: null,
	Self: false,
	PrimaryColour: "",
	SecondaryColour: "",
	TextColour: "",
	ImageFile: "",
	WatermarkImageFile: "",
	EmailPrimaryColour: "",
	EmailTextColour: "",
	EmailImageFile: "",
	ContactEmail: "",
	ContactNumber: "",
	EmailSupportData: ""
};

const WhiteLabelling = () => {
	const { reason } = useSession();
	const [whiteLabelSettings, setWhiteLabelSettings] = useState(DefaultWhiteLabellingSettings);
	const [unsavedChanges, setUnsavedChanges] = useState(false);
	const [originalSettings, setOriginalSettings] = useState(null);
	const [matchEmailColours, setMatchEmailColours] = useState(false);
	const [matchSupportEmail, setMatchSupportEmail] = useState(false);
	const [previousEmailColours, setPreviousEmailColours] = useState({
		EmailPrimaryColour: whiteLabelSettings.EmailPrimaryColour,
		EmailTextColour: whiteLabelSettings.EmailTextColour,
	});
	const [previousEmailSupportData, setPreviousEmailSupportData] = useState("");
	const [hoveredSection, setHoveredSection] = useState("");
	const [validation, setValidation] = useState({ email: true, phone: true, supportAddress: true });
	const [errorData, setErrorData] = useState({ title: "", message: "" });
	const { ready, data, execute, executingFetch } = useFetch();
	const [confirmSave, setConfirmSave] = useState(false);
	const [confirmReset, setConfirmReset] = useState(false);

	const { renderModal: renderLeaveModal } = useUnsavedChangesPrompt(unsavedChanges, {
		title: "Leave this page?",
		message: "These changes will not be saved if you leave the page. Are you sure you want to leave?",
		modalProps: {
			questionYesLabel: "Leave",
			questionYesType: ButtonType.Secondary,
			questionNoLabel: "Stay",
			questionNoType: ButtonType.Primary,
			questionReverseOrder: false
		}
	});

	const handleFetchDataRequest = (error, content) => {
		const initialSettings = {
			...DefaultWhiteLabellingSettings,
			...content
		};
		setWhiteLabelSettings(initialSettings);
		setOriginalSettings(initialSettings);
		setMatchEmailColours(initialSettings.PrimaryColour === initialSettings.EmailPrimaryColour && initialSettings.TextColour === initialSettings.EmailTextColour);
		setMatchSupportEmail(
			Boolean(initialSettings.ContactEmail) &&
			Boolean(initialSettings.EmailSupportData) &&
			initialSettings.ContactEmail === initialSettings.EmailSupportData
		);
	};

	// Initial request.
	useEffect(() => {
		if (ready) {
			execute("/api/Whitelabelling/", null, handleFetchDataRequest);
		}
	}, [ready]);

	// Effect to check unsaved changes.
	useEffect(() => {
		if (!originalSettings || !whiteLabelSettings) {
			return;
		}
		const hasChanges = !objectsEqual(whiteLabelSettings, originalSettings);
		setUnsavedChanges(hasChanges);
	}, [whiteLabelSettings, originalSettings]);

	const handleImageChange = (e, fieldName) => {
		const file = e.target.files[0];
		if (file) {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				if (file.size > IMAGE_SIZE_LIMIT) {
					setErrorData({
						title: "Could not load image",
						message: "The image file size is too large to upload - please reduce it to below 2MB and try again. This is to ensure your users on slower internet " +
							"connections don't have performance issues."
					});
				}
				else {
					setWhiteLabelSettings(prevSettings => ({
						...prevSettings,
						[fieldName]: {
							FileName: file.name,
							MimeType: file.type,
							Base64: reader.result
						}
					}));
					e.target.value = "";
				}
			};
			reader.onerror = () => {
				setErrorData({ title: "Could not load image", message: "The image could not be read. Please try again or choose a different image." });
			};
		}
	};

	// Effect to check for colour changes and update email colours when matched.
	useEffect(() => {
		if (!matchEmailColours) {
			return;
		}
		setWhiteLabelSettings(prev => ({
			...prev,
			EmailPrimaryColour: whiteLabelSettings.PrimaryColour,
			EmailTextColour: whiteLabelSettings.TextColour,
		}));
	}, [whiteLabelSettings.PrimaryColour, whiteLabelSettings.TextColour]);

	const handleSimpleSettingUpdate = (fields) => {
		setWhiteLabelSettings((prev) => {
			return { ...prev, ...fields }
		});
	};

	const handleContactEmailInputChange = (e) => {
		const email = e.target.value;
		setValidation((prev) => ({
			...prev,
			email: email.trim() === "" || isEmailValid(email),
		}));

		if (matchSupportEmail) {
			setWhiteLabelSettings(prev => ({
				...prev,
				ContactEmail: email
			}));
			setSupportAddressValue(email);
		} else {
			setWhiteLabelSettings(prev => ({
				...prev,
				ContactEmail: email
			}));
		}
	};


	const handleContactNumberInputChange = (e) => {
		const number = e.target.value;
		setValidation((prev) => ({
			...prev,
			phone: number.trim() === "" || number.length >= MINIMUM_PHONE_NUMBER_LENGTH,
		}));
		setWhiteLabelSettings(prev => ({
			...prev,
			ContactNumber: number
		}));
	};


	const handleMatchEmailColoursToggle = () => {
		const matchColours = !matchEmailColours;
		setMatchEmailColours(matchColours);

		if (matchColours) {
			setPreviousEmailColours({
				EmailPrimaryColour: whiteLabelSettings.EmailPrimaryColour,
				EmailTextColour: whiteLabelSettings.EmailTextColour,
			});
			setWhiteLabelSettings(prev => ({
				...prev,
				EmailPrimaryColour: prev.PrimaryColour,
				EmailTextColour: prev.TextColour,
			}));
		}
		else {
			// Restore previous values when unchecking
			setWhiteLabelSettings(prev => ({
				...prev,
				EmailPrimaryColour: previousEmailColours.EmailPrimaryColour,
				EmailTextColour: previousEmailColours.EmailTextColour,
			}));
		}
	};

	const handleMatchSupportEmailToggle = () => {
		const newState = !matchSupportEmail;
		setMatchSupportEmail(newState);

		if (newState) {
			setPreviousEmailSupportData(whiteLabelSettings.EmailSupportData);
			setSupportAddressValue(whiteLabelSettings.ContactEmail);
		}
		else {
			// Restore previous values when unchecking
			setSupportAddressValue(previousEmailSupportData);
		}
	};

	const handleEmailSupportAddressInputChange = (e) => {
		if (matchSupportEmail) {
			return;
		}
		const supportData = e.target.value;
		setSupportAddressValue(supportData);
	};

	const setSupportAddressValue = (value) => {
		setWhiteLabelSettings(prev => ({
			...prev,
			EmailSupportData: value,
		}));
		setValidation((prev) => ({
			...prev,
			supportAddress: !value || (isTextValid(value) && isEmailValid(value))
		}));
	};

	const handleUndo = () => {
		if (originalSettings) {
			setWhiteLabelSettings(originalSettings);
			setUnsavedChanges(false);
		}
	}

	const handleReset = (yes) => {
		if (yes) {
			const options = getFetchOptions({ method: "DELETE" });
			execute("/api/WhiteLabelling/" + whiteLabelSettings.Id, options, (err) => {
				if (err) {
					setErrorData({ title: "Could not remove White Labelling", message: "Could not remove current White Label settings and reset to parent settings." });
				}
				setUnsavedChanges(false);
				execute("/api/Whitelabelling/", null, handleFetchDataRequest);
			});
		}
		setConfirmReset(false);
	};

	const handleSave = (yes) => {
		if (yes && validation.email && validation.phone) {
				let saveWhiteLabelSettings = { ...whiteLabelSettings };
				delete saveWhiteLabelSettings.EmailWelcomeText;
				delete saveWhiteLabelSettings.EmailMessageText;
				if (!saveWhiteLabelSettings.Self) {
					saveWhiteLabelSettings.Id = -1;
				}
				const options = getFetchOptions({
					method: "POST",
					body: saveWhiteLabelSettings
				});
				execute("/api/WhiteLabelling", options, (error, content) => {
					if (error) {
						setErrorData({ title: "Could not save", message: "Could not save White Label settings." });
					}
					else {
						const makeSelf = (saveWhiteLabelSettings.Id === -1);
						const updatedSettings = {
							...whiteLabelSettings,
							Id: content,
							Self: makeSelf
						};
						handleFetchDataRequest(error, updatedSettings);
						setUnsavedChanges(false);
					}
				});
			}
		setConfirmSave(false);
	};

	const onClickSave = () => {
		setConfirmSave(true);
	}

	const onClickReset = () => {
		setConfirmReset(true);
	}

	function handleCloseErrorModal() {
		setErrorData({ title: "", message: "" });
	}

	if (reason !== LogoutReason.None) {
		return <AuthLogoutMessage reason={reason} />;
	}

	const RenderImage = (fileData, altText) => {
		if (fileData) {
			const src = (typeof (fileData) === "string") ? fileData : fileData.Base64;
			return <img src={src} alt={altText} />;
		}
		return null;
	};

	const WhiteLabellingContent = (
		<div className="white-label-container">
			<div className="white-label-container-controls">
				<h2>Images</h2>
				{/* ImageLogo upload control */}
				<div
					className="white-label-container-controls-logo control-section"
					onMouseEnter={() => setHoveredSection('logo')}
					onMouseLeave={() => setHoveredSection('')}
				>
					<h5>Logo <InfoIcon text="Image at top left of the page, should be the logo for your company." /></h5>
					<div className="image-preview">
						{RenderImage(whiteLabelSettings.ImageFile, "Logo Preview")}
					</div>
					<div className="file-upload-wrapper">
						<input type="file" accept="image/*" onChange={(e) => handleImageChange(e, "ImageFile")} />
					</div>
				</div>
				{/* ImageWatermark upload control */}
				<div
					className="white-label-container-controls-watermark control-section"
					onMouseEnter={() => setHoveredSection('watermark')}
					onMouseLeave={() => setHoveredSection('')}
				>
					<h5>Watermark <InfoIcon text="Image displayed on the right hand side of each pod." /></h5>
					<div className="watermark-radio-group">
						<label>
							<input
								type="radio"
								name="showWatermark"
								value="true"
								checked={whiteLabelSettings.ShowWatermark === true}
								onChange={() => handleSimpleSettingUpdate({ ShowWatermark: true })}
							/>
							Show
						</label>
						<label>
							<input
								type="radio"
								name="hideWatermark"
								value="false"
								checked={whiteLabelSettings.ShowWatermark === false}
								onChange={() => handleSimpleSettingUpdate({ ShowWatermark: false })}
							/>
							Hide
						</label>
					</div>

					<div className="watermark-preview" style={{ backgroundColor: whiteLabelSettings.PrimaryColour || 'transparent' }}>
						{whiteLabelSettings.Gradient && <span
							className="screen-pod-header-gradient"
							style={{
								background: `linear-gradient(90deg, rgba(0,0,0,.5) 0%, ${whiteLabelSettings.PrimaryColour} 50%, ${whiteLabelSettings.PrimaryColour} 100%)`
							}}
						></span>}
						{RenderImage(whiteLabelSettings.WatermarkImageFile, "Watermark Preview")}
					</div>
					<div className={`file-upload-wrapper ${!whiteLabelSettings.ShowWatermark ? "disabled" : ""}`}>
						<input type="file" accept="image/*" onChange={(e) => handleImageChange(e, "WatermarkImageFile")} />
					</div>
				</div>
				<h2>Colours</h2>
				{/* Colours control */}
				<div
					className="white-label-container-controls-colours control-section"
					onMouseEnter={() => setHoveredSection('colours')}
					onMouseLeave={() => setHoveredSection('')}
				>
					<ColourControl
						label="Primary Colour"
						infoText="The main colour of the website, used in the most places."
						colourKey="PrimaryColour"
						whiteLabelSettings={whiteLabelSettings}
						setWhiteLabelSettings={setWhiteLabelSettings}
					/>
					<ColourControl
						label="Secondary Colour"
						infoText="An accent colour to help important elements stand out."
						colourKey="SecondaryColour"
						whiteLabelSettings={whiteLabelSettings}
						setWhiteLabelSettings={setWhiteLabelSettings}
					/>
					<ColourControl
						label="Text on Primary Colour"
						infoText="The colour of text whenever it is placed on top of your Primary Colour."
						colourKey="TextColour"
						whiteLabelSettings={whiteLabelSettings}
						setWhiteLabelSettings={setWhiteLabelSettings}
					/>

					<div className="gradient">
						<h5>Gradient <InfoIcon text={"Places a dark to light, left to right gradient on the header of each pod."} /></h5>
						<div className="gradient-radio-group">
							<label>
								<input
									type="radio"
									name="showGradient"
									value="true"
									checked={whiteLabelSettings.Gradient === true}
									onChange={() => handleSimpleSettingUpdate({ Gradient: true })}
								/>
								Show
							</label>
							<label>
								<input
									type="radio"
									name="showGradient"
									value="false"
									checked={whiteLabelSettings.Gradient === false}
									onChange={() => handleSimpleSettingUpdate({ Gradient: false })}
								/>
								Hide
							</label>
						</div>
					</div>
				</div>
				<h2>Company Details</h2>
				{/* Company Details control */}
				<div
					className="white-label-container-controls-details control-section"
					onMouseEnter={() => setHoveredSection('details')}
					onMouseLeave={() => setHoveredSection('')}
				>
					<div className={`white-label-container-controls-details-detail ${!validation.email ? 'invalid' : ''}`}>
						<h5>Email Address <InfoIcon text={"The email address of your company that you wish to be displayed on the help page."} /></h5>
						<input id="contactEmail" type="text" value={whiteLabelSettings.ContactEmail ?? ""} onChange={handleContactEmailInputChange} placeholder="Email Address" />
					</div>
					<div className={`white-label-container-controls-details-detail ${!validation.phone ? 'invalid' : ''}`}>
						<h5>Phone Number <InfoIcon text={"The phone number of your company that you wish to be displayed on the help page."} /></h5>
						<input id="contactNumber" type="text" value={whiteLabelSettings.ContactNumber ?? ""} onChange={handleContactNumberInputChange} placeholder="Phone Number" />
					</div>
				</div>
				<h2>Email</h2>
				<div className="white-label-container-controls-email control-section" onMouseEnter={() => setHoveredSection('email')} onMouseLeave={() => setHoveredSection('')}>
					<div className="white-label-container-controls-email-upload">
						<h5>Email Banner <InfoIcon text="The image that will appear at the top of your emails." /></h5>
						<div className="image-preview">
							{RenderImage(whiteLabelSettings.EmailImageFile, "Email Header Preview")}
						</div>
						<div className="file-upload-wrapper">
							<input type="file" accept="image/*" onChange={(e) => handleImageChange(e, "EmailImageFile")} />
						</div>
					</div>
					<ColourControl
						label="Email Primary Colour"
						infoText="The colour of text whenever it is placed on top of your primary chosen colour."
						colourKey="EmailPrimaryColour"
						whiteLabelSettings={whiteLabelSettings}
						setWhiteLabelSettings={setWhiteLabelSettings}
						disabled={matchEmailColours}
					/>
					<ColourControl
						label="Button Text Colour"
						infoText="The colour of the button and details in your emails"
						colourKey="EmailTextColour"
						whiteLabelSettings={whiteLabelSettings}
						setWhiteLabelSettings={setWhiteLabelSettings}
						disabled={matchEmailColours}
					/>

					<div className="checkbox-group email-checkbox">
						<label>
							<input type="checkbox" checked={matchEmailColours} onChange={handleMatchEmailColoursToggle} />
							Match colours with above colours
						</label>
					</div>

					

					<div className={`white-label-container-controls-details-detail ${!validation.supportAddress ? 'invalid' : ''}`}>
						<h5>Support Address <InfoIcon text={"The email or website that appears in the footer of the email."} /></h5>
						<input
							type="text"
							value={whiteLabelSettings.EmailSupportData ?? ""}
							onChange={handleEmailSupportAddressInputChange}
							placeholder="Support Address"
							disabled={matchSupportEmail}
						/>
					</div>
				</div>

				<div className="checkbox-group email-checkbox">
					<label>
						<input type="checkbox" checked={matchSupportEmail} onChange={handleMatchSupportEmailToggle} />
						Match Support Address with Company Email
					</label>
				</div>

				<div className="white-label-container-controls-buttons control-section">
					{unsavedChanges && validation.email && validation.supportAddress && validation.phone &&
						<p className="unsaved">You have unsaved changes</p>
					}
					{(!validation.email) && (<p className="unsaved">You must enter a valid Email Address.</p>)}
					{(!validation.supportAddress) && (<p className="unsaved">You must enter a valid Support Email Address.</p>)}
					{(!validation.phone) && (<p className="unsaved">You must enter a Phone Number.</p>)}
					<div className="white-label-container-controls-buttons-container control-section">
						<div className="white-label-container-controls-buttons-container-button">
							<Button type={ButtonType.Secondary} label={"Undo"} onClick={handleUndo} disabled={!unsavedChanges} />
						</div>
						<div className="white-label-container-controls-buttons-container-button">
							<Button type={ButtonType.Secondary} label={"Remove White Labelling"} onClick={onClickReset} disabled={!whiteLabelSettings.Self} />
						</div>
						<div className="white-label-container-controls-buttons-container-button">
							<Button type={ButtonType.Primary} label={"Save"} onClick={onClickSave} disabled={!validation.email || !validation.phone || !validation.supportAddress || !unsavedChanges} />
						</div>
					</div>
				</div>
			</div>
			<div className="white-label-container-display">
				{/*SCREENS*/}
				<div className={`white-label-container-display-phone ${["logo", "watermark", "colours", "details", "email"].includes(hoveredSection) ? "blurred" : ""}`}>
					<Screen whiteLabelSettings={whiteLabelSettings} hoveredSection={hoveredSection} RenderImage={RenderImage} />
				</div>
				<div className={`white-label-container-display-phone ${["logo", "watermark", "colours", "details"].includes(hoveredSection) ? "blurred" : ""}`}>
					<EmailScreen whiteLabelSettings={whiteLabelSettings} hoveredSection={hoveredSection} RenderImage={RenderImage} />
				</div>
			</div>
			{errorData.message.length > 0 && (
				<MessageModal title={errorData.title} onClick={handleCloseErrorModal} size="m">
					<p>{errorData.message}</p>
				</MessageModal>
			)}
		</div>
	);

	return (
		<div className="white-label">
			<h1>White Labelling</h1>
			<p className="top-text">
				Personalise ucSKILLS to match your company branding. The live preview on this page shows you what the site will look like for your users. When you save any changes
				they will be immediately applied to all of your customers the next time they log in.
			</p>

			{(!ready || executingFetch)
				? <LoadingSpinner active={!ready || executingFetch} />
				: WhiteLabellingContent
			}

			{confirmSave &&
				<MessageModal title="Confirm Save" type={MessageModalType.Question} onClick={handleSave} size="m">
					This will update how all of your customers see ucSKILLS when they next log in. Are you sure?
				</MessageModal>
			}
			{confirmReset && 
				<MessageModal title="Confirm Reset" type={MessageModalType.Question} onClick={handleReset} size="m">
					This will reset your white label settings to their default settings and save the changes. This means everyone who has your white label settings will now be shown the default settings. Are you sure you want to reset and save?
				</MessageModal>	
			}

			{renderLeaveModal}
		</div>
	);
};

export default WhiteLabelling;
